import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { encryptApiKey, decryptApiKey } from '../utils/encryption'

const Profile = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [licenseKeys, setLicenseKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [creatingLicense, setCreatingLicense] = useState(false)
  const [showVipModal, setShowVipModal] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const [formData, setFormData] = useState({
    display_name: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    api_key: '',
    gemini_api_key: ''
  })

  const [showApiKey, setShowApiKey] = useState(false)
  const [showGeminiApiKey, setShowGeminiApiKey] = useState(false)

  useEffect(() => {
    if (user) {
      getProfile()
    }
  }, [user])

  const getProfile = async () => {
    try {
      setLoading(true)
      
      // Get profile data
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          openai (
            api_key,
            is_active
          ),
          gemini (
            api_key,
            is_active
          )
        `)
        .eq('id', user.id)
        .single()

      if (error) throw error

      setProfile(data)
      
      // Get license keys separately
      const { data: licenseData, error: licenseError } = await supabase
        .from('license_keys')
        .select(`
          id,
          license_key,
          max_machines,
          is_active,
          expires_at,
          created_at,
          machine_activations (
            machine_id,
            machine_name,
            os_info,
            activated_at,
            last_used_at,
            is_active
          )
        `)
        .eq('profile_id', user.id)

      if (licenseError) {
        console.error('Error loading license keys:', licenseError.message)
      } else {
        console.log('License data loaded:', licenseData)
        setLicenseKeys(licenseData || [])
      }

      // ถอดรหัส API key ก่อนแสดงในฟอร์ม
      const decryptedOpenAIKey = data?.openai?.[0]?.api_key ? decryptApiKey(data.openai[0].api_key) : '';
      const decryptedGeminiKey = data?.gemini?.[0]?.api_key ? decryptApiKey(data.gemini[0].api_key) : '';
      
      setFormData(prev => ({
        ...prev,
        display_name: data?.display_name || '',
        api_key: decryptedOpenAIKey,
        gemini_api_key: decryptedGeminiKey
      }))
    } catch (error) {
      console.error('Error loading profile:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    
    try {
      setUpdating(true)
      setMessage({ type: '', text: '' })

      // Update profile data
      const updates = {
        display_name: formData.display_name
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)

      if (updateError) throw updateError

      // Update OpenAI API key if provided
      if (formData.api_key) {
        // เข้ารหัส API key ก่อนบันทึกลงฐานข้อมูล
        const encryptedApiKey = encryptApiKey(formData.api_key);
        
        // Check if API key already exists
        const { data: existingKey } = await supabase
          .from('openai')
          .select('id')
          .eq('profile_id', user.id)
          .single()

        let apiKeyError
        if (existingKey) {
          // Update existing key
          const { error } = await supabase
            .from('openai')
            .update({
              api_key: encryptedApiKey,
              is_active: true
            })
            .eq('id', existingKey.id)
          apiKeyError = error
        } else {
          // Insert new key
          const { error } = await supabase
            .from('openai')
            .insert({
              profile_id: user.id,
              api_key: encryptedApiKey,
              is_active: true
            })
          apiKeyError = error
        }

        if (apiKeyError) throw apiKeyError
      }
      
      // Update Gemini API key if provided
      if (formData.gemini_api_key) {
        // เข้ารหัส API key ก่อนบันทึกลงฐานข้อมูล
        const encryptedGeminiApiKey = encryptApiKey(formData.gemini_api_key);
        
        // Check if Gemini API key already exists
        const { data: existingGeminiKey } = await supabase
          .from('gemini')
          .select('id')
          .eq('profile_id', user.id)
          .single()

        let geminiApiKeyError
        if (existingGeminiKey) {
          // Update existing key
          const { error } = await supabase
            .from('gemini')
            .update({
              api_key: encryptedGeminiApiKey,
              is_active: true
            })
            .eq('id', existingGeminiKey.id)
          geminiApiKeyError = error
        } else {
          // Insert new key
          const { error } = await supabase
            .from('gemini')
            .insert({
              profile_id: user.id,
              api_key: encryptedGeminiApiKey,
              is_active: true
            })
          geminiApiKeyError = error
        }

        if (geminiApiKeyError) throw geminiApiKeyError
      }

      // Update password if provided
      if (formData.new_password) {
        if (formData.new_password !== formData.confirm_password) {
          throw new Error('รหัสผ่านใหม่ไม่ตรงกัน')
        }

        const { error: passwordError } = await supabase.auth.updateUser({
          password: formData.new_password
        })

        if (passwordError) throw passwordError
      }

      setMessage({ type: 'success', text: 'อัพเดทข้อมูลสำเร็จ' })
      getProfile()
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setUpdating(false)
    }
  }

  const generateLicenseKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
      if ((i + 1) % 4 === 0 && i < 19) {
        result += '-';
      }
    }
    return result;
  }

  const createLicenseKey = async () => {
    try {
      setCreatingLicense(true)
      setMessage({ type: '', text: '' })

      // Check membership status
      if (!profile || !['vip', 'lifetime', 'admin'].includes(profile.membership)) {
        setShowVipModal(true)
        setCreatingLicense(false)
        return
      }

      // Generate new license key
      const newLicenseKey = generateLicenseKey()

      // Insert into database
      const { data, error } = await supabase
        .from('license_keys')
        .insert({
          license_key: newLicenseKey,
          profile_id: user.id,
          max_machines: 2,
          is_active: true
        })
        .select()
        .single()

      if (error) throw error

      // Refresh license data
      await getProfile()
      
      setMessage({ 
        type: 'success', 
        text: `สร้าง License Key สำเร็จ: ${newLicenseKey}` 
      })

    } catch (error) {
      console.error('Error creating license key:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'เกิดข้อผิดพลาดในการสร้าง License Key' 
      })
    } finally {
      setCreatingLicense(false)
    }
  }

  const deactivateMachine = async (licenseId, machineId, machineName) => {
    if (!window.confirm(`คุณต้องการลบเครื่อง "${machineName || machineId}" ออกจาก License นี้หรือไม่?`)) {
      return
    }

    try {
      const { error } = await supabase
        .from('machine_activations')
        .update({ is_active: false })
        .eq('license_key_id', licenseId)
        .eq('machine_id', machineId)

      if (error) throw error

      // Refresh license data
      await getProfile()
      
      setMessage({ 
        type: 'success', 
        text: 'ลบเครื่องออกจาก License สำเร็จ' 
      })

    } catch (error) {
      console.error('Error deactivating machine:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'เกิดข้อผิดพลาดในการลบเครื่อง' 
      })
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getMembershipText = (membership) => {
    switch (membership) {
      case 'free':
        return (
          <div className="flex items-center gap-2">
            <span className="text-gray-400">🆓</span>
            <span>สมาชิกฟรี</span>
          </div>
        )
      case 'vip':
        return (
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">👑</span>
            <span>สมาชิก VIP</span>
          </div>
        )
      case 'lifetime':
        return (
          <div className="flex items-center gap-2">
            <span className="text-green-400">♾️</span>
            <span>สมาชิกตลอดชีพ</span>
          </div>
        )
      case 'admin':
        return (
          <div className="flex items-center gap-2">
            <span className="text-red-400">⚡</span>
            <span>ผู้ดูแลระบบ</span>
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-2">
            <span>❓</span>
            <span>ไม่ระบุ</span>
          </div>
        )
    }
  }

  if (!user || loading || !profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <main className="flex-grow flex items-center justify-center">
          <div className="text-xl">กำลังโหลด...</div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <h1 className="text-2xl font-bold mb-6">ข้อมูลสมาชิก</h1>

            {/* Membership Info */}
            <div className="mb-8 p-4 bg-gray-800 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400">อีเมล</p>
                  <p className="text-lg">{profile?.email || user?.email}</p>
                </div>
                <div>
                  <p className="text-gray-400">ระดับสมาชิก</p>
                  <div className={`text-lg
                    ${profile?.membership === 'vip' && 'text-yellow-400'}
                    ${profile?.membership === 'lifetime' && 'text-green-400'}
                    ${profile?.membership === 'admin' && 'text-red-400'}
                  `}>
                    {getMembershipText(profile?.membership)}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400">วันที่สมัคร</p>
                  <p className="text-lg">{profile?.created_at ? formatDate(profile.created_at) : '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400">วันหมดอายุ</p>
                  <p className="text-lg">
                    {profile?.membership === 'vip' ? (
                      profile?.expires_at ? formatDate(profile.expires_at) : '-'
                    ) : profile?.membership === 'lifetime' ? (
                      <span className="text-green-400">ตลอดชีพ ♾️</span>
                    ) : profile?.membership === 'admin' ? (
                      <span className="text-red-400">ตลอดชีพ ⚡</span>
                    ) : (
                      '-'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* License Information */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">ข้อมูล License Key</h2>
                <button
                  onClick={createLicenseKey}
                  disabled={creatingLicense || (licenseKeys && licenseKeys.length > 0)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    licenseKeys && licenseKeys.length > 0
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : profile && ['vip', 'lifetime', 'admin'].includes(profile.membership)
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  } ${creatingLicense ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {creatingLicense ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                        <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      กำลังสร้าง...
                    </div>
                  ) : licenseKeys && licenseKeys.length > 0 ? (
                    'มี License แล้ว'
                  ) : profile && ['vip', 'lifetime', 'admin'].includes(profile.membership) ? (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      สร้าง License Key
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      ต้องการสถานะ VIP
                    </div>
                  )}
                </button>
              </div>
              
              {licenseKeys && licenseKeys.length > 0 ? (
                licenseKeys.map((license, index) => (
                  <div key={license.id} className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">License #{index + 1}</h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          license.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {license.is_active ? 'ใช้งานได้' : 'ปิดใช้งาน'}
                        </span>
                        {license.expires_at && new Date(license.expires_at) < new Date() && (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">
                            หมดอายุ
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm">License Key</p>
                        <div className="flex items-center gap-2">
                          <code className="bg-gray-900 px-2 py-1 rounded text-sm font-mono text-blue-400">
                            {license.license_key}
                          </code>
                          <button
                            onClick={() => navigator.clipboard.writeText(license.license_key)}
                            className="text-gray-400 hover:text-white transition-colors"
                            title="คัดลอก License Key"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">จำนวนเครื่องที่อนุญาต</p>
                        <p className="text-lg">{license.max_machines} เครื่อง</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">วันที่สร้าง</p>
                        <p>{formatDate(license.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">วันหมดอายุ</p>
                        <p className={license.expires_at && new Date(license.expires_at) < new Date() ? 'text-red-400' : ''}>
                          {license.expires_at ? formatDate(license.expires_at) : 'ไม่หมดอายุ'}
                        </p>
                      </div>
                    </div>

                    {/* Machine Activations */}
                    {license.machine_activations && license.machine_activations.length > 0 && (
                      <div className="border-t border-gray-700 pt-4">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                          รายการเครื่องทั้งหมด ({license.machine_activations.filter(m => m.is_active).length}/{license.max_machines} ใช้งานอยู่)
                        </h4>
                        <div className="space-y-3">
                          {license.machine_activations
                            .map((machine, machineIndex) => (
                            <div key={machineIndex} className={`p-3 rounded border ${
                              machine.is_active 
                                ? 'bg-gray-900 border-gray-600' 
                                : 'bg-gray-800 border-gray-700 opacity-75'
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    machine.is_active 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {machine.is_active ? 'ใช้งานอยู่' : 'ถูกลบแล้ว'}
                                  </span>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-gray-400">Machine ID</p>
                                  <div className="flex items-center gap-2">
                                    <code className={`bg-gray-800 px-2 py-1 rounded text-xs font-mono ${
                                      machine.is_active ? 'text-green-400' : 'text-gray-500'
                                    }`}>
                                      {machine.machine_id}
                                    </code>
                                    <button
                                      onClick={() => navigator.clipboard.writeText(machine.machine_id)}
                                      className="text-gray-400 hover:text-white transition-colors"
                                      title="คัดลอก Machine ID"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-gray-400">ชื่อเครื่อง</p>
                                  <p className={machine.is_active ? 'text-blue-300' : 'text-gray-500'}>
                                    {machine.machine_name || 'ไม่ระบุ'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-400">เปิดใช้งานเมื่อ</p>
                                  <p>{formatDate(machine.activated_at)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-400">ใช้งานล่าสุด</p>
                                  <p>{formatDate(machine.last_used_at)}</p>
                                </div>
                                {machine.os_info && (
                                  <div className="md:col-span-2">
                                    <p className="text-gray-400">ข้อมูลระบบ</p>
                                    <div className="bg-gray-800 p-2 rounded text-xs font-mono">
                                      <pre className="text-gray-300">
                                        {JSON.stringify(machine.os_info, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {machine.is_active && (
                                <div className="mt-3 flex justify-end">
                                  <button
                                    onClick={() => deactivateMachine(license.id, machine.machine_id, machine.machine_name)}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors flex items-center gap-1"
                                    title="ลบเครื่องนี้ออกจาก License"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    ลบเครื่อง
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {license.machine_activations.length === 0 && (
                          <div className="text-center text-gray-400 py-4">
                            <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                            <p>ยังไม่มีเครื่องที่ลงทะเบียน</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-6 bg-gray-800 rounded-lg border border-gray-700 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-300 mb-2">ยังไม่มี License Key</h3>
                    <p className="text-sm">คุณยังไม่มี License Key สำหรับการใช้งาน</p>
                    <p className="text-xs mt-2">ติดต่อผู้ดูแลระบบเพื่อขอ License Key</p>
                  </div>
                  <div className="mt-4 p-3 bg-gray-900 rounded text-left">
                    <p className="text-xs text-gray-400 mb-2">Debug Info:</p>
                    <p className="text-xs text-gray-500">
                      User ID: {user?.id}<br/>
                      License Keys Count: {licenseKeys ? licenseKeys.length : 'Loading...'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* VIP Required Modal */}
            {showVipModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-700">
                  <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                      <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">
                      ต้องการสถานะ VIP
                    </h3>
                    <p className="text-gray-300 mb-6">
                      คุณต้องมีสถานะ VIP, Lifetime หรือ Admin เพื่อสร้าง License Key สำหรับใช้งานระบบ
                    </p>
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        onClick={() => setShowVipModal(false)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        ปิด
                      </button>
                      <button
                        onClick={() => {
                          setShowVipModal(false)
                          // Navigate to VIP page - you may need to use react-router's navigate
                          window.location.href = '/vip'
                        }}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        อัพเกรดเป็น VIP
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Update Form */}
            <form onSubmit={handleUpdate} className="space-y-6">
              {message.text && (
                <div className={`mb-4 p-3 rounded ${
                  message.type === 'error' ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'
                }`}>
                  {message.text}
                </div>
              )}

              <div className="space-y-6">
                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    ชื่อที่แสดง
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={e => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* OpenAI API Key */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    OpenAI API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={formData.api_key}
                      onChange={e => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="sk-..."
                      className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showApiKey ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-400">
                    API key จะถูกเข้ารหัสและเก็บไว้อย่างปลอดภัย
                  </p>
                </div>
                
                {/* Gemini API Key */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gemini API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showGeminiApiKey ? "text" : "password"}
                      value={formData.gemini_api_key}
                      onChange={e => setFormData(prev => ({ ...prev, gemini_api_key: e.target.value }))}
                      placeholder="AIza..."
                      className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGeminiApiKey(!showGeminiApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showGeminiApiKey ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-400">
                    Gemini API key จะถูกเข้ารหัสและเก็บไว้อย่างปลอดภัย
                  </p>
                </div>

                {/* Change Password */}
                <div className="border-t border-gray-700 pt-6">
                  <h3 className="text-lg font-medium mb-4">เปลี่ยนรหัสผ่าน</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        รหัสผ่านใหม่
                      </label>
                      <input
                        type="password"
                        value={formData.new_password}
                        onChange={e => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        ยืนยันรหัสผ่านใหม่
                      </label>
                      <input
                        type="password"
                        value={formData.confirm_password}
                        onChange={e => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {updating ? 'กำลังอัพเดท...' : 'บันทึกการเปลี่ยนแปลง'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Profile